import { create } from 'zustand'
import { nanoid } from 'nanoid'
import { TimeRange, TimeRangeOrPeriod } from 'src/types'
import { ExcludedPeriod as ModelExcludedPeriod } from 'src/services'

export interface ExcludedPeriod {
  id: string
  timeRange?: TimeRange
  hidden: boolean
}
interface TrainingPeriodStore {
  modelId?: string
  isExcludedPeriodOpen: boolean
  excludedPeriods: ExcludedPeriod[]
  trainingPeriod: TimeRangeOrPeriod
  isModified: boolean
  isEdit: boolean
  setIsExcludedPeriodOpen: (isOpen: boolean) => void
  setTrainingPeriod: (period: TimeRangeOrPeriod) => void
  addExcludedPeriod: (timeRange?: TimeRange) => void
  removeExcludedPeriod: (id: string) => void
  editExcludedPeriod: (id: string, updateData: SetTimePeriod) => void
  setIsEdit: (isEdit: boolean) => void
  resetState: (model: ResetStateInput) => void
}

interface ResetStateInput {
  id: string
  trainingStart?: Date
  trainingEnd?: Date
  trainingPeriod?: string
  excludedPeriods?: ModelExcludedPeriod[]
}

type SetTimePeriod = {
  timeRange?: TimeRange
  hidden?: boolean
}

export const useTrainingPeriodStore = create<TrainingPeriodStore>(set => ({
  isExcludedPeriodOpen: false,
  isModified: false,
  isEdit: false,
  excludedPeriods: [],
  trainingPeriod: { period: 'P1Y' },
  setIsExcludedPeriodOpen: isOpen => set({ isExcludedPeriodOpen: isOpen }),
  addExcludedPeriod: timeRange =>
    set(state => ({
      excludedPeriods: [
        ...state.excludedPeriods,
        { id: nanoid(), timeRange, hidden: false },
      ],
    })),

  setTrainingPeriod: period =>
    set({ trainingPeriod: period, isModified: true }),

  setIsEdit: isEdit => set({ isEdit }),

  removeExcludedPeriod: id =>
    set(state => ({
      excludedPeriods: state.excludedPeriods.filter(period => period.id !== id),
      isModified: true,
    })),

  editExcludedPeriod: (id, updateData) =>
    set(state => ({
      isModified: updateData.timeRange ? true : state.isModified,
      excludedPeriods: state.excludedPeriods.map(period =>
        period.id === id
          ? {
              ...period,
              timeRange: updateData.timeRange ?? period.timeRange,
              hidden: updateData.hidden ?? period.hidden,
            }
          : period,
      ),
    })),

  resetState: model =>
    set({
      modelId: model.id,
      isExcludedPeriodOpen: (model.excludedPeriods?.length ?? 0) > 0,
      isModified: false,
      isEdit: model.excludedPeriods?.length === 0,
      excludedPeriods:
        model.excludedPeriods && model.excludedPeriods.length > 0
          ? model.excludedPeriods?.map(period => ({
              id: nanoid(),
              timeRange: {
                from: period.start.valueOf(),
                to: period.end.valueOf(),
              },
              hidden: false,
            }))
          : [
              {
                id: nanoid(),
                timeRange: undefined,
                hidden: false,
              },
            ],
      trainingPeriod:
        model.trainingStart && model.trainingEnd
          ? {
              from: model.trainingStart.valueOf(),
              to: model.trainingEnd.valueOf(),
            }
          : { period: model.trainingPeriod || 'P1Y' },
    }),
}))
